import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  StrictMode,
} from 'react'

type Props = Record<string, unknown>

/**
 * Inject one or more props on a set of `children`. This is useful for things
 * like automatically applying a theme to all children.
 *
 * @example
 * export const Bucket: FC<BucketProps> = ({
 *   children,
 *   // ...
 *   theme = 'light',
 * }) => (
 *   There’s a hole in my bucket. 🙁
 *
 *   <ChildrenWithInjectedProps theme={theme}>
 *     {children}
 *   </ChildrenWithInjectedProps>
 * );
 *
 * @note It would be nice to define this component as a generic so callers can
 *   specify the expected props shape (for example,
 *   `<ChildrenWithInjectedProps<ChildProps> ...>`), but it doesn’t seem like
 *   TypeScript currently supports this, or at least doesn’t support it with
 *   reasonable syntax.
 *   https://dev.to/michaeljota/using-typescript-generics-with-your-react-components-4dde
 */

export const ChildrenWithInjectedProps = forwardRef<unknown, Props>(
  // this throws a misleading error if ref argument is not present
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props, ref) => {
    const { children, ...forwardedProps } = props

    return (
      <StrictMode>
        {children &&
          Children.map(children, (child) => {
            if (isValidElement(child))
              return cloneElement(child, forwardedProps)
          })}
      </StrictMode>
    )
  }
)
ChildrenWithInjectedProps.displayName = 'ChildrenWithInjectedProps'
